// @ts-strict-ignore
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { RouterModule } from '@angular/router';

import { DataObject, StoreBootstrap } from '@app/_shared/interfaces';
import { ThemeService } from '@app/_shared/service/theme.service';
import { loadDashboardStatistics, loadInitialDashboardStatistics } from '@app/dashboard/store/dashboard.actions';
import {
  getActiveStores,
  getDefaultStore,
  hasFailedBookkeepingJob,
  isSaving,
} from '@app/main-store/bootstrap.selector';
import { createSubscription } from '@app/main-store/subscriptions/subscription.actions';
import { FaDuotoneIconComponent, FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TranslocoModule } from '@jsverse/transloco';
import { Store } from '@ngrx/store';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { MessageModule } from 'primeng/message';
import { Observable } from 'rxjs/internal/Observable';
import { map } from 'rxjs/operators';
import * as fromDashboardSelectors from './store/dashboard.selectors';

@Component({
  selector: 'sb-dashboard',
  templateUrl: './dashboard.component.html',

  imports: [
    CommonModule,
    RouterModule,
    TranslocoModule,
    CardModule,
    ButtonModule,
    MessageModule,
    FontAwesomeModule,
    FaDuotoneIconComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardComponent implements OnInit {
  private rxStore = inject<Store<any>>(Store);
  private themeService = inject(ThemeService);

  public isDarkMode = this.themeService.isDarkMode();

  public defaultStore$: Observable<DataObject<StoreBootstrap>> = this.rxStore.select(getDefaultStore);
  public defaultStoreSubscription$ = this.defaultStore$.pipe(map((store) => store?.attributes?.subscriptions));
  public savingSubscription$: Observable<boolean> = this.rxStore.select(isSaving);
  public activeStoresCount$: Observable<number> = this.rxStore
    .select(getActiveStores)
    .pipe(map((stores) => stores.length));
  public bookkeepingJobsCount$: Observable<number> = this.rxStore.select(
    fromDashboardSelectors.selectBookkeepingJobsCount,
  );
  public ordersCount$: Observable<number> = this.rxStore.select(fromDashboardSelectors.selectOrdersCount);
  public integrationsCount$: Observable<number> = this.rxStore.select(fromDashboardSelectors.selectIntegrationsCount);
  public lastUpdatedAt$: Observable<string> = this.rxStore.select(fromDashboardSelectors.selectLastUpdatedAt).pipe(
    map((date) => {
      if (date === undefined) {
        return ' - ';
      }

      return date.toFormat('HH:mm');
    }),
  );
  public loading$: Observable<boolean> = this.rxStore.select(fromDashboardSelectors.selectLoadingStatistics);
  public hasFailedBookkeepingJob$: Observable<boolean> = this.rxStore.select(hasFailedBookkeepingJob);

  constructor() {
    this.themeService.themeMode$.subscribe((mode) => {
      this.isDarkMode = mode === 'dark';
    });
  }

  ngOnInit() {
    this.rxStore.dispatch(loadInitialDashboardStatistics());
  }

  onReactivateSubscription(storeId: number) {
    this.rxStore.dispatch(
      createSubscription({
        data: { store_id: storeId },
      }),
    );
  }

  onLoadStatistics() {
    this.rxStore.dispatch(loadDashboardStatistics());
  }
}
